const BASE_PATH = "http://localhost:5000".replace(/\/+$/, "");
const isBlob = (value) => typeof Blob !== "undefined" && value instanceof Blob;
class BaseAPI {
  constructor(configuration = new Configuration()) {
    this.configuration = configuration;
    this.fetchApi = async (url, init) => {
      let fetchParams = { url, init };
      for (const middleware of this.middleware) {
        if (middleware.pre) {
          fetchParams = await middleware.pre({
            fetch: this.fetchApi,
            ...fetchParams
          }) || fetchParams;
        }
      }
      let response = await this.configuration.fetchApi(fetchParams.url, fetchParams.init);
      for (const middleware of this.middleware) {
        if (middleware.post) {
          response = await middleware.post({
            fetch: this.fetchApi,
            url,
            init,
            response: response.clone()
          }) || response;
        }
      }
      return response;
    };
    this.middleware = configuration.middleware;
  }
  withMiddleware(...middlewares) {
    const next = this.clone();
    next.middleware = next.middleware.concat(...middlewares);
    return next;
  }
  withPreMiddleware(...preMiddlewares) {
    const middlewares = preMiddlewares.map((pre) => ({ pre }));
    return this.withMiddleware(...middlewares);
  }
  withPostMiddleware(...postMiddlewares) {
    const middlewares = postMiddlewares.map((post) => ({ post }));
    return this.withMiddleware(...middlewares);
  }
  async request(context) {
    const { url, init } = this.createFetchParams(context);
    const response = await this.fetchApi(url, init);
    if (response.status >= 200 && response.status < 300) {
      return response;
    }
    throw response;
  }
  createFetchParams(context) {
    let url = this.configuration.basePath + context.path;
    if (context.query !== void 0 && Object.keys(context.query).length !== 0) {
      url += "?" + this.configuration.queryParamsStringify(context.query);
    }
    const body = typeof FormData !== "undefined" && context.body instanceof FormData || context.body instanceof URLSearchParams || isBlob(context.body) ? context.body : JSON.stringify(context.body);
    const headers = Object.assign({}, this.configuration.headers, context.headers);
    const init = {
      method: context.method,
      headers,
      body,
      credentials: this.configuration.credentials
    };
    return { url, init };
  }
  clone() {
    const constructor = this.constructor;
    const next = new constructor(this.configuration);
    next.middleware = this.middleware.slice();
    return next;
  }
}
class RequiredError extends Error {
  constructor(field, msg) {
    super(msg);
    this.field = field;
    this.name = "RequiredError";
  }
}
const COLLECTION_FORMATS = {
  csv: ",",
  ssv: " ",
  tsv: "	",
  pipes: "|"
};
class Configuration {
  constructor(configuration = {}) {
    this.configuration = configuration;
  }
  get basePath() {
    return this.configuration.basePath != null ? this.configuration.basePath : BASE_PATH;
  }
  get fetchApi() {
    return globalThis.fetch.bind(globalThis);
  }
  get middleware() {
    return this.configuration.middleware || [];
  }
  get queryParamsStringify() {
    return this.configuration.queryParamsStringify || querystring;
  }
  get username() {
    return this.configuration.username;
  }
  get password() {
    return this.configuration.password;
  }
  get apiKey() {
    const apiKey = this.configuration.apiKey;
    if (apiKey) {
      return typeof apiKey === "function" ? apiKey : () => apiKey;
    }
    return void 0;
  }
  get accessToken() {
    const accessToken = this.configuration.accessToken;
    if (accessToken) {
      return typeof accessToken === "function" ? accessToken : () => accessToken;
    }
    return void 0;
  }
  get headers() {
    return this.configuration.headers;
  }
  get credentials() {
    return this.configuration.credentials;
  }
}
function exists(json, key) {
  const value = json[key];
  return value !== null && value !== void 0;
}
function querystring(params, prefix = "") {
  return Object.keys(params).map((key) => {
    const fullKey = prefix + (prefix.length ? `[${key}]` : key);
    const value = params[key];
    if (value instanceof Array) {
      const multiValue = value.map((singleValue) => encodeURIComponent(String(singleValue))).join(`&${encodeURIComponent(fullKey)}=`);
      return `${encodeURIComponent(fullKey)}=${multiValue}`;
    }
    if (value instanceof Date) {
      return `${encodeURIComponent(fullKey)}=${encodeURIComponent(value.toISOString())}`;
    }
    if (value instanceof Object) {
      return querystring(value, fullKey);
    }
    return `${encodeURIComponent(fullKey)}=${encodeURIComponent(String(value))}`;
  }).filter((part) => part.length > 0).join("&");
}
function mapValues(data, fn) {
  return Object.keys(data).reduce((acc, key) => ({ ...acc, [key]: fn(data[key]) }), {});
}
function canConsumeForm(consumes) {
  for (const consume of consumes) {
    if (consume.contentType === "multipart/form-data") {
      return true;
    }
  }
  return false;
}
class JSONApiResponse {
  constructor(raw, transformer = (jsonValue) => jsonValue) {
    this.raw = raw;
    this.transformer = transformer;
  }
  async value() {
    return this.transformer(await this.raw.json());
  }
}
class VoidApiResponse {
  constructor(raw) {
    this.raw = raw;
  }
  async value() {
    return void 0;
  }
}
class BlobApiResponse {
  constructor(raw) {
    this.raw = raw;
  }
  async value() {
    return await this.raw.blob();
  }
}
class TextApiResponse {
  constructor(raw) {
    this.raw = raw;
  }
  async value() {
    return await this.raw.text();
  }
}

class AccountantsApi extends BaseAPI {
  async apiV1AccountantsAccountantIdDeleteRaw(requestParameters) {
    if (requestParameters.accountantId === null || requestParameters.accountantId === void 0) {
      throw new RequiredError("accountantId", "Required parameter requestParameters.accountantId was null or undefined when calling apiV1AccountantsAccountantIdDelete.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Accountants/{accountantId}`.replace(`{${"accountantId"}}`, encodeURIComponent(String(requestParameters.accountantId))),
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters
    });
    return new VoidApiResponse(response);
  }
  async apiV1AccountantsAccountantIdDelete(requestParameters) {
    await this.apiV1AccountantsAccountantIdDeleteRaw(requestParameters);
  }
  async apiV1AccountantsDeleteRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Accountants`,
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.accountantDeleteRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1AccountantsDelete(requestParameters) {
    await this.apiV1AccountantsDeleteRaw(requestParameters);
  }
  async apiV1AccountantsGetRaw(requestParameters) {
    const queryParameters = {};
    if (requestParameters.auctionAccessIds) {
      queryParameters["auctionAccessIds"] = requestParameters.auctionAccessIds;
    }
    if (requestParameters.organizationIds) {
      queryParameters["organizationIds"] = requestParameters.organizationIds;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Accountants`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    });
    return new JSONApiResponse(response);
  }
  async apiV1AccountantsGet(requestParameters) {
    const response = await this.apiV1AccountantsGetRaw(requestParameters);
    return await response.value();
  }
  async apiV1AccountantsPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Accountants`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.accountantCreateRequest
    });
    return new JSONApiResponse(response);
  }
  async apiV1AccountantsPost(requestParameters) {
    const response = await this.apiV1AccountantsPostRaw(requestParameters);
    return await response.value();
  }
}

class AddressesApi extends BaseAPI {
  async apiV1AddressesAuctionHubNameOrganizationIdGetRaw(requestParameters) {
    if (requestParameters.auctionHubName === null || requestParameters.auctionHubName === void 0) {
      throw new RequiredError("auctionHubName", "Required parameter requestParameters.auctionHubName was null or undefined when calling apiV1AddressesAuctionHubNameOrganizationIdGet.");
    }
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV1AddressesAuctionHubNameOrganizationIdGet.");
    }
    const queryParameters = {};
    if (requestParameters.addressTypes) {
      queryParameters["addressTypes"] = requestParameters.addressTypes;
    }
    if (requestParameters.bypassDeduping !== void 0) {
      queryParameters["bypassDeduping"] = requestParameters.bypassDeduping;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Addresses/{auctionHubName}/{organizationId}`.replace(`{${"auctionHubName"}}`, encodeURIComponent(String(requestParameters.auctionHubName))).replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    });
    return new JSONApiResponse(response);
  }
  async apiV1AddressesAuctionHubNameOrganizationIdGet(requestParameters) {
    const response = await this.apiV1AddressesAuctionHubNameOrganizationIdGetRaw(requestParameters);
    return await response.value();
  }
  async apiV1AddressesDeleteRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Addresses`,
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.organizationAddressRemoveRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1AddressesDelete(requestParameters) {
    await this.apiV1AddressesDeleteRaw(requestParameters);
  }
  async apiV1AddressesDetailsAddressIdGetRaw(requestParameters) {
    if (requestParameters.addressId === null || requestParameters.addressId === void 0) {
      throw new RequiredError("addressId", "Required parameter requestParameters.addressId was null or undefined when calling apiV1AddressesDetailsAddressIdGet.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Addresses/Details/{addressId}`.replace(`{${"addressId"}}`, encodeURIComponent(String(requestParameters.addressId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    });
    return new JSONApiResponse(response);
  }
  async apiV1AddressesDetailsAddressIdGet(requestParameters) {
    const response = await this.apiV1AddressesDetailsAddressIdGetRaw(requestParameters);
    return await response.value();
  }
  async apiV1AddressesGetRaw(requestParameters) {
    const queryParameters = {};
    if (requestParameters.organizationIds) {
      queryParameters["organizationIds"] = requestParameters.organizationIds;
    }
    if (requestParameters.auctionAccessIds) {
      queryParameters["auctionAccessIds"] = requestParameters.auctionAccessIds;
    }
    if (requestParameters.addressTypes) {
      queryParameters["addressTypes"] = requestParameters.addressTypes;
    }
    if (requestParameters.bypassDeduping !== void 0) {
      queryParameters["bypassDeduping"] = requestParameters.bypassDeduping;
    }
    if (requestParameters.trimZipCode !== void 0) {
      queryParameters["trimZipCode"] = requestParameters.trimZipCode;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Addresses`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    });
    return new JSONApiResponse(response);
  }
  async apiV1AddressesGet(requestParameters) {
    const response = await this.apiV1AddressesGetRaw(requestParameters);
    return await response.value();
  }
  async apiV1AddressesMovePostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Addresses/Move`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.addressMoveRequest
    });
    return new JSONApiResponse(response);
  }
  async apiV1AddressesMovePost(requestParameters) {
    const response = await this.apiV1AddressesMovePostRaw(requestParameters);
    return await response.value();
  }
  async apiV1AddressesOrganizationIdAddressIdPatchRaw(requestParameters) {
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV1AddressesOrganizationIdAddressIdPatch.");
    }
    if (requestParameters.addressId === null || requestParameters.addressId === void 0) {
      throw new RequiredError("addressId", "Required parameter requestParameters.addressId was null or undefined when calling apiV1AddressesOrganizationIdAddressIdPatch.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Addresses/{organizationId}/{addressId}`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))).replace(`{${"addressId"}}`, encodeURIComponent(String(requestParameters.addressId))),
      method: "PATCH",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.addressRequestJsonPatchDocument
    });
    return new JSONApiResponse(response);
  }
  async apiV1AddressesOrganizationIdAddressIdPatch(requestParameters) {
    const response = await this.apiV1AddressesOrganizationIdAddressIdPatchRaw(requestParameters);
    return await response.value();
  }
  async apiV1AddressesOrganizationIdAddressIdPutRaw(requestParameters) {
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV1AddressesOrganizationIdAddressIdPut.");
    }
    if (requestParameters.addressId === null || requestParameters.addressId === void 0) {
      throw new RequiredError("addressId", "Required parameter requestParameters.addressId was null or undefined when calling apiV1AddressesOrganizationIdAddressIdPut.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Addresses/{organizationId}/{addressId}`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))).replace(`{${"addressId"}}`, encodeURIComponent(String(requestParameters.addressId))),
      method: "PUT",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.addressRequest
    });
    return new JSONApiResponse(response);
  }
  async apiV1AddressesOrganizationIdAddressIdPut(requestParameters) {
    const response = await this.apiV1AddressesOrganizationIdAddressIdPutRaw(requestParameters);
    return await response.value();
  }
  async apiV1AddressesOrganizationIdGetRaw(requestParameters) {
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV1AddressesOrganizationIdGet.");
    }
    const queryParameters = {};
    if (requestParameters.addressTypes) {
      queryParameters["addressTypes"] = requestParameters.addressTypes;
    }
    if (requestParameters.bypassDeduping !== void 0) {
      queryParameters["bypassDeduping"] = requestParameters.bypassDeduping;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Addresses/{organizationId}`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    });
    return new JSONApiResponse(response);
  }
  async apiV1AddressesOrganizationIdGet(requestParameters) {
    const response = await this.apiV1AddressesOrganizationIdGetRaw(requestParameters);
    return await response.value();
  }
  async apiV1AddressesOrganizationIdPostRaw(requestParameters) {
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV1AddressesOrganizationIdPost.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Addresses/{organizationId}`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.addressRequest
    });
    return new JSONApiResponse(response);
  }
  async apiV1AddressesOrganizationIdPost(requestParameters) {
    const response = await this.apiV1AddressesOrganizationIdPostRaw(requestParameters);
    return await response.value();
  }
  async apiV1AddressesPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Addresses`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.addressRequest
    });
    return new JSONApiResponse(response);
  }
  async apiV1AddressesPost(requestParameters) {
    const response = await this.apiV1AddressesPostRaw(requestParameters);
    return await response.value();
  }
  async apiV1AddressesSanitizeAddressIdPostRaw(requestParameters) {
    if (requestParameters.addressId === null || requestParameters.addressId === void 0) {
      throw new RequiredError("addressId", "Required parameter requestParameters.addressId was null or undefined when calling apiV1AddressesSanitizeAddressIdPost.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Addresses/Sanitize/{addressId}`.replace(`{${"addressId"}}`, encodeURIComponent(String(requestParameters.addressId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    });
    return new VoidApiResponse(response);
  }
  async apiV1AddressesSanitizeAddressIdPost(requestParameters) {
    await this.apiV1AddressesSanitizeAddressIdPostRaw(requestParameters);
  }
  async apiV1AddressesSourceDeleteRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Addresses/source`,
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.sourceDeleteRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1AddressesSourceDelete(requestParameters) {
    await this.apiV1AddressesSourceDeleteRaw(requestParameters);
  }
}

class AuthApi extends BaseAPI {
  async apiV1AuthEmailGetRaw(requestParameters) {
    if (requestParameters.email === null || requestParameters.email === void 0) {
      throw new RequiredError("email", "Required parameter requestParameters.email was null or undefined when calling apiV1AuthEmailGet.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Auth/{email}`.replace(`{${"email"}}`, encodeURIComponent(String(requestParameters.email))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    });
    return new JSONApiResponse(response);
  }
  async apiV1AuthEmailGet(requestParameters) {
    const response = await this.apiV1AuthEmailGetRaw(requestParameters);
    return await response.value();
  }
  async apiV1AuthPasswordForgotPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Auth/Password/Forgot`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.forgotPasswordRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1AuthPasswordForgotPost(requestParameters) {
    await this.apiV1AuthPasswordForgotPostRaw(requestParameters);
  }
  async apiV1AuthPasswordUpdatePostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Auth/Password/Update`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.passwordChangeRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1AuthPasswordUpdatePost(requestParameters) {
    await this.apiV1AuthPasswordUpdatePostRaw(requestParameters);
  }
  async apiV1AuthPasswordUserForgotPostRaw(requestParameters) {
    const queryParameters = {};
    if (requestParameters.redirectUrl !== void 0) {
      queryParameters["redirectUrl"] = requestParameters.redirectUrl;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Auth/Password/User/Forgot`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    });
    return new VoidApiResponse(response);
  }
  async apiV1AuthPasswordUserForgotPost(requestParameters) {
    await this.apiV1AuthPasswordUserForgotPostRaw(requestParameters);
  }
  async apiV1AuthPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Auth`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.userRegistrationRequest
    });
    return new JSONApiResponse(response);
  }
  async apiV1AuthPost(requestParameters) {
    const response = await this.apiV1AuthPostRaw(requestParameters);
    return await response.value();
  }
}

class CheckoutPreferencesApi extends BaseAPI {
  async apiV1CheckoutPreferencesAuctionAccessAuctionAccessIdGetRaw(requestParameters) {
    if (requestParameters.auctionAccessId === null || requestParameters.auctionAccessId === void 0) {
      throw new RequiredError("auctionAccessId", "Required parameter requestParameters.auctionAccessId was null or undefined when calling apiV1CheckoutPreferencesAuctionAccessAuctionAccessIdGet.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/CheckoutPreferences/AuctionAccess/{auctionAccessId}`.replace(`{${"auctionAccessId"}}`, encodeURIComponent(String(requestParameters.auctionAccessId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    });
    return new JSONApiResponse(response);
  }
  async apiV1CheckoutPreferencesAuctionAccessAuctionAccessIdGet(requestParameters) {
    const response = await this.apiV1CheckoutPreferencesAuctionAccessAuctionAccessIdGetRaw(requestParameters);
    return await response.value();
  }
  async apiV1CheckoutPreferencesOrgOrganizationIdGetRaw(requestParameters) {
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV1CheckoutPreferencesOrgOrganizationIdGet.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/CheckoutPreferences/Org/{organizationId}`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    });
    return new JSONApiResponse(response);
  }
  async apiV1CheckoutPreferencesOrgOrganizationIdGet(requestParameters) {
    const response = await this.apiV1CheckoutPreferencesOrgOrganizationIdGetRaw(requestParameters);
    return await response.value();
  }
}

class DebugApi extends BaseAPI {
  async apiV1DebugAddAddressTypeAddressTypePostRaw(requestParameters) {
    if (requestParameters.addressType === null || requestParameters.addressType === void 0) {
      throw new RequiredError("addressType", "Required parameter requestParameters.addressType was null or undefined when calling apiV1DebugAddAddressTypeAddressTypePost.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/AddAddressType/{addressType}`.replace(`{${"addressType"}}`, encodeURIComponent(String(requestParameters.addressType))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.orgAddressIds
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugAddAddressTypeAddressTypePost(requestParameters) {
    await this.apiV1DebugAddAddressTypeAddressTypePostRaw(requestParameters);
  }
  async apiV1DebugAddressDupCleanupPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/AddressDupCleanup`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.addressDuplicateInfo
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugAddressDupCleanupPost(requestParameters) {
    await this.apiV1DebugAddressDupCleanupPostRaw(requestParameters);
  }
  async apiV1DebugAuthSchemaPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/AuthSchema`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.body
    });
    return new TextApiResponse(response);
  }
  async apiV1DebugAuthSchemaPost(requestParameters) {
    const response = await this.apiV1DebugAuthSchemaPostRaw(requestParameters);
    return await response.value();
  }
  async apiV1DebugAuthTenantPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/AuthTenant`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.body
    });
    return new TextApiResponse(response);
  }
  async apiV1DebugAuthTenantPost(requestParameters) {
    const response = await this.apiV1DebugAuthTenantPostRaw(requestParameters);
    return await response.value();
  }
  async apiV1DebugBulkSanitizeAddressesGetRaw() {
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/BulkSanitizeAddresses`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    });
    return new JSONApiResponse(response);
  }
  async apiV1DebugBulkSanitizeAddressesGet() {
    const response = await this.apiV1DebugBulkSanitizeAddressesGetRaw();
    return await response.value();
  }
  async apiV1DebugCleanupTaxInfoPostRaw() {
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/CleanupTaxInfo`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugCleanupTaxInfoPost() {
    await this.apiV1DebugCleanupTaxInfoPostRaw();
  }
  async apiV1DebugClearEnrolledProgramsPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/ClearEnrolledPrograms`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.clearEnrolledProgramsRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugClearEnrolledProgramsPost(requestParameters) {
    await this.apiV1DebugClearEnrolledProgramsPostRaw(requestParameters);
  }
  async apiV1DebugCopyPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/Copy`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.copyRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugCopyPost(requestParameters) {
    await this.apiV1DebugCopyPostRaw(requestParameters);
  }
  async apiV1DebugDeleteOrgProcessingMessagePostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/DeleteOrgProcessingMessage`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.body
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugDeleteOrgProcessingMessagePost(requestParameters) {
    await this.apiV1DebugDeleteOrgProcessingMessagePostRaw(requestParameters);
  }
  async apiV1DebugEregQaDataResetPostRaw() {
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/EregQaDataReset`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugEregQaDataResetPost() {
    await this.apiV1DebugEregQaDataResetPostRaw();
  }
  async apiV1DebugExportBusinessUnitEntitiesPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/ExportBusinessUnitEntities`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.exportBusinessUnitEntitiesRequest
    });
    return new TextApiResponse(response);
  }
  async apiV1DebugExportBusinessUnitEntitiesPost(requestParameters) {
    const response = await this.apiV1DebugExportBusinessUnitEntitiesPostRaw(requestParameters);
    return await response.value();
  }
  async apiV1DebugGetDeadLettersGetRaw(requestParameters) {
    if (requestParameters.pageNumber === null || requestParameters.pageNumber === void 0) {
      throw new RequiredError("pageNumber", "Required parameter requestParameters.pageNumber was null or undefined when calling apiV1DebugGetDeadLettersGet.");
    }
    const queryParameters = {};
    if (requestParameters.pageSize !== void 0) {
      queryParameters["pageSize"] = requestParameters.pageSize;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/GetDeadLetters`.replace(`{${"pageNumber"}}`, encodeURIComponent(String(requestParameters.pageNumber))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    });
    return new JSONApiResponse(response);
  }
  async apiV1DebugGetDeadLettersGet(requestParameters) {
    const response = await this.apiV1DebugGetDeadLettersGetRaw(requestParameters);
    return await response.value();
  }
  async apiV1DebugImportCheckoutPreferencesPostRaw(requestParameters) {
    if (requestParameters.file === null || requestParameters.file === void 0) {
      throw new RequiredError("file", "Required parameter requestParameters.file was null or undefined when calling apiV1DebugImportCheckoutPreferencesPost.");
    }
    if (requestParameters.mode === null || requestParameters.mode === void 0) {
      throw new RequiredError("mode", "Required parameter requestParameters.mode was null or undefined when calling apiV1DebugImportCheckoutPreferencesPost.");
    }
    if (requestParameters.region === null || requestParameters.region === void 0) {
      throw new RequiredError("region", "Required parameter requestParameters.region was null or undefined when calling apiV1DebugImportCheckoutPreferencesPost.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const consumes = [{ contentType: "multipart/form-data" }];
    const canConsumeForm$1 = canConsumeForm(consumes);
    let formParams;
    let useForm = false;
    useForm = canConsumeForm$1;
    if (useForm) {
      formParams = new FormData();
    } else {
      formParams = new URLSearchParams();
    }
    if (requestParameters.file !== void 0) {
      formParams.append("File", requestParameters.file);
    }
    if (requestParameters.mode !== void 0) {
      formParams.append("Mode", new Blob([JSON.stringify(requestParameters.mode)], { type: "application/json" }));
    }
    if (requestParameters.region !== void 0) {
      formParams.append("Region", new Blob([JSON.stringify(requestParameters.region)], { type: "application/json" }));
    }
    const response = await this.request({
      path: `/api/v1/Debug/ImportCheckoutPreferences`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: formParams
    });
    return new JSONApiResponse(response);
  }
  async apiV1DebugImportCheckoutPreferencesPost(requestParameters) {
    const response = await this.apiV1DebugImportCheckoutPreferencesPostRaw(requestParameters);
    return await response.value();
  }
  async apiV1DebugInitializeEnrollmentsPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/InitializeEnrollments`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.enrollmentRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugInitializeEnrollmentsPost(requestParameters) {
    await this.apiV1DebugInitializeEnrollmentsPostRaw(requestParameters);
  }
  async apiV1DebugInvalidateProfileCacheCcMasterIdPostRaw(requestParameters) {
    if (requestParameters.ccMasterId === null || requestParameters.ccMasterId === void 0) {
      throw new RequiredError("ccMasterId", "Required parameter requestParameters.ccMasterId was null or undefined when calling apiV1DebugInvalidateProfileCacheCcMasterIdPost.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/InvalidateProfileCache/{ccMasterId}`.replace(`{${"ccMasterId"}}`, encodeURIComponent(String(requestParameters.ccMasterId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugInvalidateProfileCacheCcMasterIdPost(requestParameters) {
    await this.apiV1DebugInvalidateProfileCacheCcMasterIdPostRaw(requestParameters);
  }
  async apiV1DebugMarkTradeRevEftAccountsAsSellerPostRaw(requestParameters) {
    const queryParameters = {};
    if (requestParameters.limit !== void 0) {
      queryParameters["limit"] = requestParameters.limit;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/MarkTradeRevEftAccountsAsSeller`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugMarkTradeRevEftAccountsAsSellerPost(requestParameters) {
    await this.apiV1DebugMarkTradeRevEftAccountsAsSellerPostRaw(requestParameters);
  }
  async apiV1DebugMigrateCheckoutPreferencesPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/MigrateCheckoutPreferences`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.migrateCheckoutPreferencesRequest
    });
    return new JSONApiResponse(response);
  }
  async apiV1DebugMigrateCheckoutPreferencesPost(requestParameters) {
    const response = await this.apiV1DebugMigrateCheckoutPreferencesPostRaw(requestParameters);
    return await response.value();
  }
  async apiV1DebugParentChildMergeTypePostRaw(requestParameters) {
    if (requestParameters.mergeType === null || requestParameters.mergeType === void 0) {
      throw new RequiredError("mergeType", "Required parameter requestParameters.mergeType was null or undefined when calling apiV1DebugParentChildMergeTypePost.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/ParentChild/{mergeType}`.replace(`{${"mergeType"}}`, encodeURIComponent(String(requestParameters.mergeType))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.mergeRecord
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugParentChildMergeTypePost(requestParameters) {
    await this.apiV1DebugParentChildMergeTypePostRaw(requestParameters);
  }
  async apiV1DebugPopulateAuthServerAllPostRaw(requestParameters) {
    const queryParameters = {};
    if (requestParameters.clearExisting !== void 0) {
      queryParameters["clearExisting"] = requestParameters.clearExisting;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/PopulateAuthServer/All`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugPopulateAuthServerAllPost(requestParameters) {
    await this.apiV1DebugPopulateAuthServerAllPostRaw(requestParameters);
  }
  async apiV1DebugPopulateAuthServerPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/PopulateAuthServer`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.authorizationPopulateRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugPopulateAuthServerPost(requestParameters) {
    await this.apiV1DebugPopulateAuthServerPostRaw(requestParameters);
  }
  async apiV1DebugPopulateNewFieldsPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/PopulateNewFields`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.populateNewFieldsRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugPopulateNewFieldsPost(requestParameters) {
    await this.apiV1DebugPopulateNewFieldsPostRaw(requestParameters);
  }
  async apiV1DebugPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.accountantCreateRequest
    });
    return new JSONApiResponse(response);
  }
  async apiV1DebugPost(requestParameters) {
    const response = await this.apiV1DebugPostRaw(requestParameters);
    return await response.value();
  }
  async apiV1DebugRePublishDeadLettersPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/RePublishDeadLetters`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.republishDeadLettersRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugRePublishDeadLettersPost(requestParameters) {
    await this.apiV1DebugRePublishDeadLettersPostRaw(requestParameters);
  }
  async apiV1DebugRemoveBidderRolePostRaw() {
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/RemoveBidderRole`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugRemoveBidderRolePost() {
    await this.apiV1DebugRemoveBidderRolePostRaw();
  }
  async apiV1DebugRemoveSourceIdsPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/RemoveSourceIds`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.removeSourceIdsRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugRemoveSourceIdsPost(requestParameters) {
    await this.apiV1DebugRemoveSourceIdsPostRaw(requestParameters);
  }
  async apiV1DebugReplaySalesforceEventsPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/ReplaySalesforceEvents`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.salesforceReplayRequest
    });
    return new JSONApiResponse(response);
  }
  async apiV1DebugReplaySalesforceEventsPost(requestParameters) {
    const response = await this.apiV1DebugReplaySalesforceEventsPostRaw(requestParameters);
    return await response.value();
  }
  async apiV1DebugRepublishAddressDeadLettersPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/RepublishAddressDeadLetters`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.republishDeadLettersRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugRepublishAddressDeadLettersPost(requestParameters) {
    await this.apiV1DebugRepublishAddressDeadLettersPostRaw(requestParameters);
  }
  async apiV1DebugRepublishChangeEventsPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/RepublishChangeEvents`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.republishChangeEventRequest
    });
    return new JSONApiResponse(response);
  }
  async apiV1DebugRepublishChangeEventsPost(requestParameters) {
    const response = await this.apiV1DebugRepublishChangeEventsPostRaw(requestParameters);
    return await response.value();
  }
  async apiV1DebugRepublishOrganizationDeadLettersPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/RepublishOrganizationDeadLetters`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.republishDeadLettersRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugRepublishOrganizationDeadLettersPost(requestParameters) {
    await this.apiV1DebugRepublishOrganizationDeadLettersPostRaw(requestParameters);
  }
  async apiV1DebugRepublishOrganizationGroupDeadLettersPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/RepublishOrganizationGroupDeadLetters`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.republishDeadLettersRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugRepublishOrganizationGroupDeadLettersPost(requestParameters) {
    await this.apiV1DebugRepublishOrganizationGroupDeadLettersPostRaw(requestParameters);
  }
  async apiV1DebugRepublishPaymentMethodDeadLettersPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/RepublishPaymentMethodDeadLetters`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.republishDeadLettersRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugRepublishPaymentMethodDeadLettersPost(requestParameters) {
    await this.apiV1DebugRepublishPaymentMethodDeadLettersPostRaw(requestParameters);
  }
  async apiV1DebugRepublishRepDeadLettersPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/RepublishRepDeadLetters`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.republishDeadLettersRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugRepublishRepDeadLettersPost(requestParameters) {
    await this.apiV1DebugRepublishRepDeadLettersPostRaw(requestParameters);
  }
  async apiV1DebugRepublishSFDeadLettersPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/RepublishSFDeadLetters`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.republishDeadLettersRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugRepublishSFDeadLettersPost(requestParameters) {
    await this.apiV1DebugRepublishSFDeadLettersPostRaw(requestParameters);
  }
  async apiV1DebugResyncDataMergeTypePostRaw(requestParameters) {
    if (requestParameters.mergeType === null || requestParameters.mergeType === void 0) {
      throw new RequiredError("mergeType", "Required parameter requestParameters.mergeType was null or undefined when calling apiV1DebugResyncDataMergeTypePost.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/ResyncData/{mergeType}`.replace(`{${"mergeType"}}`, encodeURIComponent(String(requestParameters.mergeType))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.requestBody
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugResyncDataMergeTypePost(requestParameters) {
    await this.apiV1DebugResyncDataMergeTypePostRaw(requestParameters);
  }
  async apiV1DebugRollbackSyncPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/RollbackSync`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.rollbackRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugRollbackSyncPost(requestParameters) {
    await this.apiV1DebugRollbackSyncPostRaw(requestParameters);
  }
  async apiV1DebugSeedAllGroundingAddressesPostRaw() {
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/SeedAllGroundingAddresses`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugSeedAllGroundingAddressesPost() {
    await this.apiV1DebugSeedAllGroundingAddressesPostRaw();
  }
  async apiV1DebugSeedCheckoutPreferencesPostRaw(requestParameters) {
    const queryParameters = {};
    if (requestParameters.autoCheckoutEnabled !== void 0) {
      queryParameters["autoCheckoutEnabled"] = requestParameters.autoCheckoutEnabled;
    }
    if (requestParameters.dryRun !== void 0) {
      queryParameters["dryRun"] = requestParameters.dryRun;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const consumes = [{ contentType: "multipart/form-data" }];
    const canConsumeForm$1 = canConsumeForm(consumes);
    let formParams;
    let useForm = false;
    useForm = canConsumeForm$1;
    if (useForm) {
      formParams = new FormData();
    } else {
      formParams = new URLSearchParams();
    }
    if (requestParameters.file !== void 0) {
      formParams.append("file", requestParameters.file);
    }
    const response = await this.request({
      path: `/api/v1/Debug/SeedCheckoutPreferences`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: formParams
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugSeedCheckoutPreferencesPost(requestParameters) {
    await this.apiV1DebugSeedCheckoutPreferencesPostRaw(requestParameters);
  }
  async apiV1DebugSeedGroundingAddressesPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/SeedGroundingAddresses`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.requestBody
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugSeedGroundingAddressesPost(requestParameters) {
    await this.apiV1DebugSeedGroundingAddressesPostRaw(requestParameters);
  }
  async apiV1DebugSeedProfitCenterPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/SeedProfitCenter`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.profitCenter
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugSeedProfitCenterPost(requestParameters) {
    await this.apiV1DebugSeedProfitCenterPostRaw(requestParameters);
  }
  async apiV1DebugSendTestDocumentPostRaw(requestParameters) {
    const queryParameters = {};
    if (requestParameters.email !== void 0) {
      queryParameters["email"] = requestParameters.email;
    }
    if (requestParameters.clientUserId !== void 0) {
      queryParameters["clientUserId"] = requestParameters.clientUserId;
    }
    if (requestParameters.redirectUrl !== void 0) {
      queryParameters["redirectUrl"] = requestParameters.redirectUrl;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/SendTestDocument`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    });
    return new TextApiResponse(response);
  }
  async apiV1DebugSendTestDocumentPost(requestParameters) {
    const response = await this.apiV1DebugSendTestDocumentPostRaw(requestParameters);
    return await response.value();
  }
  async apiV1DebugSetAddressesAsPrimaryPostRaw(requestParameters) {
    const queryParameters = {};
    if (requestParameters.createRelation !== void 0) {
      queryParameters["createRelation"] = requestParameters.createRelation;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const consumes = [{ contentType: "multipart/form-data" }];
    const canConsumeForm$1 = canConsumeForm(consumes);
    let formParams;
    let useForm = false;
    useForm = canConsumeForm$1;
    if (useForm) {
      formParams = new FormData();
    } else {
      formParams = new URLSearchParams();
    }
    if (requestParameters.file !== void 0) {
      formParams.append("file", requestParameters.file);
    }
    const response = await this.request({
      path: `/api/v1/Debug/SetAddressesAsPrimary`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: formParams
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugSetAddressesAsPrimaryPost(requestParameters) {
    await this.apiV1DebugSetAddressesAsPrimaryPostRaw(requestParameters);
  }
  async apiV1DebugSetCheckoutApplicationFlagsPostRaw(requestParameters) {
    const queryParameters = {};
    if (requestParameters.appliesToTr !== void 0) {
      queryParameters["appliesToTr"] = requestParameters.appliesToTr;
    }
    if (requestParameters.appliesToVlc !== void 0) {
      queryParameters["appliesToVlc"] = requestParameters.appliesToVlc;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const consumes = [{ contentType: "multipart/form-data" }];
    const canConsumeForm$1 = canConsumeForm(consumes);
    let formParams;
    let useForm = false;
    useForm = canConsumeForm$1;
    if (useForm) {
      formParams = new FormData();
    } else {
      formParams = new URLSearchParams();
    }
    if (requestParameters.file !== void 0) {
      formParams.append("file", requestParameters.file);
    }
    const response = await this.request({
      path: `/api/v1/Debug/SetCheckoutApplicationFlags`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: formParams
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugSetCheckoutApplicationFlagsPost(requestParameters) {
    await this.apiV1DebugSetCheckoutApplicationFlagsPostRaw(requestParameters);
  }
  async apiV1DebugSetRepTypePostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/SetRepType`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.repTypeRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugSetRepTypePost(requestParameters) {
    await this.apiV1DebugSetRepTypePostRaw(requestParameters);
  }
  async apiV1DebugSetVlcLocationsPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/SetVlcLocations`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.vlcLocationRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugSetVlcLocationsPost(requestParameters) {
    await this.apiV1DebugSetVlcLocationsPostRaw(requestParameters);
  }
  async apiV1DebugSwapSourceIdsPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/SwapSourceIds`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.swapSourceIdsRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugSwapSourceIdsPost(requestParameters) {
    await this.apiV1DebugSwapSourceIdsPostRaw(requestParameters);
  }
  async apiV1DebugSyncMergedDataMergeTypePostRaw(requestParameters) {
    if (requestParameters.mergeType === null || requestParameters.mergeType === void 0) {
      throw new RequiredError("mergeType", "Required parameter requestParameters.mergeType was null or undefined when calling apiV1DebugSyncMergedDataMergeTypePost.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/SyncMergedData/{mergeType}`.replace(`{${"mergeType"}}`, encodeURIComponent(String(requestParameters.mergeType))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.mergeSync
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugSyncMergedDataMergeTypePost(requestParameters) {
    await this.apiV1DebugSyncMergedDataMergeTypePostRaw(requestParameters);
  }
  async apiV1DebugSyncMergedDataPostRaw() {
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Debug/SyncMergedData`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    });
    return new VoidApiResponse(response);
  }
  async apiV1DebugSyncMergedDataPost() {
    await this.apiV1DebugSyncMergedDataPostRaw();
  }
}

class DefaultsApi extends BaseAPI {
  async apiV1DefaultsDefaultIdDeleteRaw(requestParameters) {
    if (requestParameters.defaultId === null || requestParameters.defaultId === void 0) {
      throw new RequiredError("defaultId", "Required parameter requestParameters.defaultId was null or undefined when calling apiV1DefaultsDefaultIdDelete.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Defaults/{defaultId}`.replace(`{${"defaultId"}}`, encodeURIComponent(String(requestParameters.defaultId))),
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters
    });
    return new VoidApiResponse(response);
  }
  async apiV1DefaultsDefaultIdDelete(requestParameters) {
    await this.apiV1DefaultsDefaultIdDeleteRaw(requestParameters);
  }
  async apiV1DefaultsOrgOrganizationIdDefaultTransportAddressPutRaw(requestParameters) {
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV1DefaultsOrgOrganizationIdDefaultTransportAddressPut.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Defaults/Org/{organizationId}/DefaultTransportAddress`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))),
      method: "PUT",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.updateDefaultTransportRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1DefaultsOrgOrganizationIdDefaultTransportAddressPut(requestParameters) {
    await this.apiV1DefaultsOrgOrganizationIdDefaultTransportAddressPutRaw(requestParameters);
  }
  async apiV1DefaultsOrgOrganizationIdPutRaw(requestParameters) {
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV1DefaultsOrgOrganizationIdPut.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Defaults/Org/{organizationId}`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))),
      method: "PUT",
      headers: headerParameters,
      query: queryParameters
    });
    return new VoidApiResponse(response);
  }
  async apiV1DefaultsOrgOrganizationIdPut(requestParameters) {
    await this.apiV1DefaultsOrgOrganizationIdPutRaw(requestParameters);
  }
  async apiV1DefaultsOrgRepIdOrganizationIdPutRaw(requestParameters) {
    if (requestParameters.repId === null || requestParameters.repId === void 0) {
      throw new RequiredError("repId", "Required parameter requestParameters.repId was null or undefined when calling apiV1DefaultsOrgRepIdOrganizationIdPut.");
    }
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV1DefaultsOrgRepIdOrganizationIdPut.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Defaults/Org/{repId}/{organizationId}`.replace(`{${"repId"}}`, encodeURIComponent(String(requestParameters.repId))).replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))),
      method: "PUT",
      headers: headerParameters,
      query: queryParameters
    });
    return new VoidApiResponse(response);
  }
  async apiV1DefaultsOrgRepIdOrganizationIdPut(requestParameters) {
    await this.apiV1DefaultsOrgRepIdOrganizationIdPutRaw(requestParameters);
  }
  async apiV1DefaultsOrganizationIdGetRaw(requestParameters) {
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV1DefaultsOrganizationIdGet.");
    }
    const queryParameters = {};
    if (requestParameters.defaultType !== void 0) {
      queryParameters["defaultType"] = requestParameters.defaultType;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Defaults/{organizationId}`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    });
    return new JSONApiResponse(response);
  }
  async apiV1DefaultsOrganizationIdGet(requestParameters) {
    const response = await this.apiV1DefaultsOrganizationIdGetRaw(requestParameters);
    return await response.value();
  }
  async apiV1DefaultsOrganizationIdPutRaw(requestParameters) {
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV1DefaultsOrganizationIdPut.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Defaults/{organizationId}`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))),
      method: "PUT",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.defaultRequest
    });
    return new JSONApiResponse(response);
  }
  async apiV1DefaultsOrganizationIdPut(requestParameters) {
    const response = await this.apiV1DefaultsOrganizationIdPutRaw(requestParameters);
    return await response.value();
  }
}

class DocumentsApi extends BaseAPI {
  async apiV1DocumentsDocIdGetRaw(requestParameters) {
    if (requestParameters.docId === null || requestParameters.docId === void 0) {
      throw new RequiredError("docId", "Required parameter requestParameters.docId was null or undefined when calling apiV1DocumentsDocIdGet.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Documents/{docId}`.replace(`{${"docId"}}`, encodeURIComponent(String(requestParameters.docId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    });
    return new JSONApiResponse(response);
  }
  async apiV1DocumentsDocIdGet(requestParameters) {
    const response = await this.apiV1DocumentsDocIdGetRaw(requestParameters);
    return await response.value();
  }
  async apiV1DocumentsDocIdPutRaw(requestParameters) {
    if (requestParameters.docId === null || requestParameters.docId === void 0) {
      throw new RequiredError("docId", "Required parameter requestParameters.docId was null or undefined when calling apiV1DocumentsDocIdPut.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Documents/{docId}`.replace(`{${"docId"}}`, encodeURIComponent(String(requestParameters.docId))),
      method: "PUT",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.updateDocumentRequest
    });
    return new JSONApiResponse(response);
  }
  async apiV1DocumentsDocIdPut(requestParameters) {
    const response = await this.apiV1DocumentsDocIdPutRaw(requestParameters);
    return await response.value();
  }
  async apiV1DocumentsDocIdViewGetRaw(requestParameters) {
    if (requestParameters.docId === null || requestParameters.docId === void 0) {
      throw new RequiredError("docId", "Required parameter requestParameters.docId was null or undefined when calling apiV1DocumentsDocIdViewGet.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Documents/{docId}/View`.replace(`{${"docId"}}`, encodeURIComponent(String(requestParameters.docId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    });
    return new TextApiResponse(response);
  }
  async apiV1DocumentsDocIdViewGet(requestParameters) {
    const response = await this.apiV1DocumentsDocIdViewGetRaw(requestParameters);
    return await response.value();
  }
  async apiV1DocumentsPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Documents`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.createDocumentRequest
    });
    return new JSONApiResponse(response);
  }
  async apiV1DocumentsPost(requestParameters) {
    const response = await this.apiV1DocumentsPostRaw(requestParameters);
    return await response.value();
  }
  async apiV1DocumentsSendPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Documents/Send`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.documentRequest
    });
    return new JSONApiResponse(response);
  }
  async apiV1DocumentsSendPost(requestParameters) {
    const response = await this.apiV1DocumentsSendPostRaw(requestParameters);
    return await response.value();
  }
  async apiV1DocumentsUrlEnvelopeIdPostRaw(requestParameters) {
    if (requestParameters.envelopeId === null || requestParameters.envelopeId === void 0) {
      throw new RequiredError("envelopeId", "Required parameter requestParameters.envelopeId was null or undefined when calling apiV1DocumentsUrlEnvelopeIdPost.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Documents/Url/{envelopeId}`.replace(`{${"envelopeId"}}`, encodeURIComponent(String(requestParameters.envelopeId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.documentRequest
    });
    return new JSONApiResponse(response);
  }
  async apiV1DocumentsUrlEnvelopeIdPost(requestParameters) {
    const response = await this.apiV1DocumentsUrlEnvelopeIdPostRaw(requestParameters);
    return await response.value();
  }
}

class IdentityApi extends BaseAPI {
  async apiV1IdentityPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Identity`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.identifierAddRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1IdentityPost(requestParameters) {
    await this.apiV1IdentityPostRaw(requestParameters);
  }
  async apiV1IdentitySourceObjectTypeSourceIdGetRaw(requestParameters) {
    if (requestParameters.objectType === null || requestParameters.objectType === void 0) {
      throw new RequiredError("objectType", "Required parameter requestParameters.objectType was null or undefined when calling apiV1IdentitySourceObjectTypeSourceIdGet.");
    }
    if (requestParameters.sourceId === null || requestParameters.sourceId === void 0) {
      throw new RequiredError("sourceId", "Required parameter requestParameters.sourceId was null or undefined when calling apiV1IdentitySourceObjectTypeSourceIdGet.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Identity/Source/{objectType}/{sourceId}`.replace(`{${"objectType"}}`, encodeURIComponent(String(requestParameters.objectType))).replace(`{${"sourceId"}}`, encodeURIComponent(String(requestParameters.sourceId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    });
    return new JSONApiResponse(response);
  }
  async apiV1IdentitySourceObjectTypeSourceIdGet(requestParameters) {
    const response = await this.apiV1IdentitySourceObjectTypeSourceIdGetRaw(requestParameters);
    return await response.value();
  }
  async apiV1IdentitySourceSystemIdGetRaw(requestParameters) {
    if (requestParameters.sourceSystem === null || requestParameters.sourceSystem === void 0) {
      throw new RequiredError("sourceSystem", "Required parameter requestParameters.sourceSystem was null or undefined when calling apiV1IdentitySourceSystemIdGet.");
    }
    if (requestParameters.id === null || requestParameters.id === void 0) {
      throw new RequiredError("id", "Required parameter requestParameters.id was null or undefined when calling apiV1IdentitySourceSystemIdGet.");
    }
    const queryParameters = {};
    if (requestParameters.region !== void 0) {
      queryParameters["region"] = requestParameters.region;
    }
    if (requestParameters.identifiers) {
      queryParameters["Identifiers"] = requestParameters.identifiers;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Identity/{sourceSystem}/{id}`.replace(`{${"sourceSystem"}}`, encodeURIComponent(String(requestParameters.sourceSystem))).replace(`{${"id"}}`, encodeURIComponent(String(requestParameters.id))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    });
    return new JSONApiResponse(response);
  }
  async apiV1IdentitySourceSystemIdGet(requestParameters) {
    const response = await this.apiV1IdentitySourceSystemIdGetRaw(requestParameters);
    return await response.value();
  }
}

class OrganizationsApi extends BaseAPI {
  async apiV1OrganizationsGetInvitedRepsGetRaw(requestParameters) {
    const queryParameters = {};
    if (requestParameters.orgId !== void 0) {
      queryParameters["orgId"] = requestParameters.orgId;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Organizations/GetInvitedReps`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    });
    return new JSONApiResponse(response);
  }
  async apiV1OrganizationsGetInvitedRepsGet(requestParameters) {
    const response = await this.apiV1OrganizationsGetInvitedRepsGetRaw(requestParameters);
    return await response.value();
  }
  async apiV1OrganizationsGetRepsGetRaw(requestParameters) {
    const queryParameters = {};
    if (requestParameters.orgId !== void 0) {
      queryParameters["orgId"] = requestParameters.orgId;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Organizations/GetReps`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    });
    return new JSONApiResponse(response);
  }
  async apiV1OrganizationsGetRepsGet(requestParameters) {
    const response = await this.apiV1OrganizationsGetRepsGetRaw(requestParameters);
    return await response.value();
  }
  async apiV1OrganizationsInviteRepPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Organizations/InviteRep`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.repInviteRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1OrganizationsInviteRepPost(requestParameters) {
    await this.apiV1OrganizationsInviteRepPostRaw(requestParameters);
  }
  async apiV1OrganizationsMergePostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Organizations/Merge`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.orgMergeRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1OrganizationsMergePost(requestParameters) {
    await this.apiV1OrganizationsMergePostRaw(requestParameters);
  }
  async apiV1OrganizationsOrganizationIdGetRaw(requestParameters) {
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV1OrganizationsOrganizationIdGet.");
    }
    const queryParameters = {};
    if (requestParameters.mergedView !== void 0) {
      queryParameters["mergedView"] = requestParameters.mergedView;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Organizations/{organizationId}`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    });
    return new JSONApiResponse(response);
  }
  async apiV1OrganizationsOrganizationIdGet(requestParameters) {
    const response = await this.apiV1OrganizationsOrganizationIdGetRaw(requestParameters);
    return await response.value();
  }
  async apiV1OrganizationsOrganizationIdPatchRaw(requestParameters) {
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV1OrganizationsOrganizationIdPatch.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Organizations/{organizationId}`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))),
      method: "PATCH",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.organizationUpdateRequestJsonPatchDocument
    });
    return new JSONApiResponse(response);
  }
  async apiV1OrganizationsOrganizationIdPatch(requestParameters) {
    const response = await this.apiV1OrganizationsOrganizationIdPatchRaw(requestParameters);
    return await response.value();
  }
  async apiV1OrganizationsOrganizationIdPutRaw(requestParameters) {
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV1OrganizationsOrganizationIdPut.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Organizations/{organizationId}`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))),
      method: "PUT",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.organizationUpdateRequest
    });
    return new JSONApiResponse(response);
  }
  async apiV1OrganizationsOrganizationIdPut(requestParameters) {
    const response = await this.apiV1OrganizationsOrganizationIdPutRaw(requestParameters);
    return await response.value();
  }
  async apiV1OrganizationsOrganizationIdStatusPutRaw(requestParameters) {
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV1OrganizationsOrganizationIdStatusPut.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Organizations/{organizationId}/Status`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))),
      method: "PUT",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.statusRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1OrganizationsOrganizationIdStatusPut(requestParameters) {
    await this.apiV1OrganizationsOrganizationIdStatusPutRaw(requestParameters);
  }
  async apiV1OrganizationsPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Organizations`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.organizationCreateRequest
    });
    return new JSONApiResponse(response);
  }
  async apiV1OrganizationsPost(requestParameters) {
    const response = await this.apiV1OrganizationsPostRaw(requestParameters);
    return await response.value();
  }
  async apiV1OrganizationsSetStripeTokensPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Organizations/SetStripeTokens`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.setStripeTokenRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1OrganizationsSetStripeTokensPost(requestParameters) {
    await this.apiV1OrganizationsSetStripeTokensPostRaw(requestParameters);
  }
  async apiV1OrganizationsUnmergePostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Organizations/Unmerge`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.unmergeRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1OrganizationsUnmergePost(requestParameters) {
    await this.apiV1OrganizationsUnmergePostRaw(requestParameters);
  }
  async apiV1OrganizationsUpdateRepRolesPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Organizations/UpdateRepRoles`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.updateRepRolesRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1OrganizationsUpdateRepRolesPost(requestParameters) {
    await this.apiV1OrganizationsUpdateRepRolesPostRaw(requestParameters);
  }
  async apiV1OrganizationsVerifyPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Organizations/Verify`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.verifyOrganizationRequest
    });
    return new JSONApiResponse(response);
  }
  async apiV1OrganizationsVerifyPost(requestParameters) {
    const response = await this.apiV1OrganizationsVerifyPostRaw(requestParameters);
    return await response.value();
  }
}

class PaymentMethodsApi extends BaseAPI {
  async apiV1PaymentMethodsAuctionAccessAaIdGetRaw(requestParameters) {
    if (requestParameters.aaId === null || requestParameters.aaId === void 0) {
      throw new RequiredError("aaId", "Required parameter requestParameters.aaId was null or undefined when calling apiV1PaymentMethodsAuctionAccessAaIdGet.");
    }
    const queryParameters = {};
    if (requestParameters.sourceSystem !== void 0) {
      queryParameters["sourceSystem"] = requestParameters.sourceSystem;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/PaymentMethods/AuctionAccess/{aaId}`.replace(`{${"aaId"}}`, encodeURIComponent(String(requestParameters.aaId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    });
    return new JSONApiResponse(response);
  }
  async apiV1PaymentMethodsAuctionAccessAaIdGet(requestParameters) {
    const response = await this.apiV1PaymentMethodsAuctionAccessAaIdGetRaw(requestParameters);
    return await response.value();
  }
  async apiV1PaymentMethodsBulkOrganizationIdPostRaw(requestParameters) {
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV1PaymentMethodsBulkOrganizationIdPost.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/PaymentMethods/Bulk/{organizationId}`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.paymentMethodRequest
    });
    return new JSONApiResponse(response);
  }
  async apiV1PaymentMethodsBulkOrganizationIdPost(requestParameters) {
    const response = await this.apiV1PaymentMethodsBulkOrganizationIdPostRaw(requestParameters);
    return await response.value();
  }
  async apiV1PaymentMethodsBulkPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/PaymentMethods/Bulk`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.getPaymentAccountRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1PaymentMethodsBulkPost(requestParameters) {
    await this.apiV1PaymentMethodsBulkPostRaw(requestParameters);
  }
  async apiV1PaymentMethodsDeleteRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/PaymentMethods`,
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.sourceDeleteRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1PaymentMethodsDelete(requestParameters) {
    await this.apiV1PaymentMethodsDeleteRaw(requestParameters);
  }
  async apiV1PaymentMethodsListOrganizationIdGetRaw(requestParameters) {
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV1PaymentMethodsListOrganizationIdGet.");
    }
    const queryParameters = {};
    if (requestParameters.sourceSystem !== void 0) {
      queryParameters["sourceSystem"] = requestParameters.sourceSystem;
    }
    if (requestParameters.includeByAuctionAccessId !== void 0) {
      queryParameters["includeByAuctionAccessId"] = requestParameters.includeByAuctionAccessId;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/PaymentMethods/List/{organizationId}`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    });
    return new JSONApiResponse(response);
  }
  async apiV1PaymentMethodsListOrganizationIdGet(requestParameters) {
    const response = await this.apiV1PaymentMethodsListOrganizationIdGetRaw(requestParameters);
    return await response.value();
  }
  async apiV1PaymentMethodsListPendingOrganizationIdGetRaw(requestParameters) {
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV1PaymentMethodsListPendingOrganizationIdGet.");
    }
    const queryParameters = {};
    if (requestParameters.includeByAuctionAccessId !== void 0) {
      queryParameters["includeByAuctionAccessId"] = requestParameters.includeByAuctionAccessId;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/PaymentMethods/List/Pending/{organizationId}`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    });
    return new JSONApiResponse(response);
  }
  async apiV1PaymentMethodsListPendingOrganizationIdGet(requestParameters) {
    const response = await this.apiV1PaymentMethodsListPendingOrganizationIdGetRaw(requestParameters);
    return await response.value();
  }
  async apiV1PaymentMethodsOrganizationIdCreditCardsCardIdDeleteRaw(requestParameters) {
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV1PaymentMethodsOrganizationIdCreditCardsCardIdDelete.");
    }
    if (requestParameters.cardId === null || requestParameters.cardId === void 0) {
      throw new RequiredError("cardId", "Required parameter requestParameters.cardId was null or undefined when calling apiV1PaymentMethodsOrganizationIdCreditCardsCardIdDelete.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/PaymentMethods/{organizationId}/CreditCards/{cardId}`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))).replace(`{${"cardId"}}`, encodeURIComponent(String(requestParameters.cardId))),
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters
    });
    return new VoidApiResponse(response);
  }
  async apiV1PaymentMethodsOrganizationIdCreditCardsCardIdDelete(requestParameters) {
    await this.apiV1PaymentMethodsOrganizationIdCreditCardsCardIdDeleteRaw(requestParameters);
  }
  async apiV1PaymentMethodsOrganizationIdCreditCardsCardIdPutRaw(requestParameters) {
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV1PaymentMethodsOrganizationIdCreditCardsCardIdPut.");
    }
    if (requestParameters.cardId === null || requestParameters.cardId === void 0) {
      throw new RequiredError("cardId", "Required parameter requestParameters.cardId was null or undefined when calling apiV1PaymentMethodsOrganizationIdCreditCardsCardIdPut.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/PaymentMethods/{organizationId}/CreditCards/{cardId}`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))).replace(`{${"cardId"}}`, encodeURIComponent(String(requestParameters.cardId))),
      method: "PUT",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.updateCardRequest
    });
    return new JSONApiResponse(response);
  }
  async apiV1PaymentMethodsOrganizationIdCreditCardsCardIdPut(requestParameters) {
    const response = await this.apiV1PaymentMethodsOrganizationIdCreditCardsCardIdPutRaw(requestParameters);
    return await response.value();
  }
  async apiV1PaymentMethodsOrganizationIdCreditCardsGetRaw(requestParameters) {
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV1PaymentMethodsOrganizationIdCreditCardsGet.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/PaymentMethods/{organizationId}/CreditCards`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    });
    return new JSONApiResponse(response);
  }
  async apiV1PaymentMethodsOrganizationIdCreditCardsGet(requestParameters) {
    const response = await this.apiV1PaymentMethodsOrganizationIdCreditCardsGetRaw(requestParameters);
    return await response.value();
  }
  async apiV1PaymentMethodsOrganizationIdCreditCardsPostRaw(requestParameters) {
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV1PaymentMethodsOrganizationIdCreditCardsPost.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/PaymentMethods/{organizationId}/CreditCards`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.addCardRequest
    });
    return new JSONApiResponse(response);
  }
  async apiV1PaymentMethodsOrganizationIdCreditCardsPost(requestParameters) {
    const response = await this.apiV1PaymentMethodsOrganizationIdCreditCardsPostRaw(requestParameters);
    return await response.value();
  }
  async apiV1PaymentMethodsOrganizationIdPostRaw(requestParameters) {
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV1PaymentMethodsOrganizationIdPost.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/PaymentMethods/{organizationId}`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.paymentMethodRequest
    });
    return new JSONApiResponse(response);
  }
  async apiV1PaymentMethodsOrganizationIdPost(requestParameters) {
    const response = await this.apiV1PaymentMethodsOrganizationIdPostRaw(requestParameters);
    return await response.value();
  }
  async apiV1PaymentMethodsOrganizationIdSetPaymentSettingsPutRaw(requestParameters) {
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV1PaymentMethodsOrganizationIdSetPaymentSettingsPut.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/PaymentMethods/{organizationId}/SetPaymentSettings`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))),
      method: "PUT",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.updatePaymentSettingsRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1PaymentMethodsOrganizationIdSetPaymentSettingsPut(requestParameters) {
    await this.apiV1PaymentMethodsOrganizationIdSetPaymentSettingsPutRaw(requestParameters);
  }
  async apiV1PaymentMethodsPaymentAccountIdGetRaw(requestParameters) {
    if (requestParameters.paymentAccountId === null || requestParameters.paymentAccountId === void 0) {
      throw new RequiredError("paymentAccountId", "Required parameter requestParameters.paymentAccountId was null or undefined when calling apiV1PaymentMethodsPaymentAccountIdGet.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/PaymentMethods/{paymentAccountId}`.replace(`{${"paymentAccountId"}}`, encodeURIComponent(String(requestParameters.paymentAccountId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    });
    return new JSONApiResponse(response);
  }
  async apiV1PaymentMethodsPaymentAccountIdGet(requestParameters) {
    const response = await this.apiV1PaymentMethodsPaymentAccountIdGetRaw(requestParameters);
    return await response.value();
  }
  async apiV1PaymentMethodsPaymentMethodIdDeleteRaw(requestParameters) {
    if (requestParameters.paymentMethodId === null || requestParameters.paymentMethodId === void 0) {
      throw new RequiredError("paymentMethodId", "Required parameter requestParameters.paymentMethodId was null or undefined when calling apiV1PaymentMethodsPaymentMethodIdDelete.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/PaymentMethods/{paymentMethodId}`.replace(`{${"paymentMethodId"}}`, encodeURIComponent(String(requestParameters.paymentMethodId))),
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters
    });
    return new VoidApiResponse(response);
  }
  async apiV1PaymentMethodsPaymentMethodIdDelete(requestParameters) {
    await this.apiV1PaymentMethodsPaymentMethodIdDeleteRaw(requestParameters);
  }
  async apiV1PaymentMethodsPendingAuctionAccessAaIdGetRaw(requestParameters) {
    if (requestParameters.aaId === null || requestParameters.aaId === void 0) {
      throw new RequiredError("aaId", "Required parameter requestParameters.aaId was null or undefined when calling apiV1PaymentMethodsPendingAuctionAccessAaIdGet.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/PaymentMethods/Pending/AuctionAccess/{aaId}`.replace(`{${"aaId"}}`, encodeURIComponent(String(requestParameters.aaId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    });
    return new JSONApiResponse(response);
  }
  async apiV1PaymentMethodsPendingAuctionAccessAaIdGet(requestParameters) {
    const response = await this.apiV1PaymentMethodsPendingAuctionAccessAaIdGetRaw(requestParameters);
    return await response.value();
  }
  async apiV1PaymentMethodsPlaidBalancePaymentAccountIdGetRaw(requestParameters) {
    if (requestParameters.paymentAccountId === null || requestParameters.paymentAccountId === void 0) {
      throw new RequiredError("paymentAccountId", "Required parameter requestParameters.paymentAccountId was null or undefined when calling apiV1PaymentMethodsPlaidBalancePaymentAccountIdGet.");
    }
    const queryParameters = {};
    if (requestParameters.forceCheck !== void 0) {
      queryParameters["forceCheck"] = requestParameters.forceCheck;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/PaymentMethods/Plaid/Balance/{paymentAccountId}`.replace(`{${"paymentAccountId"}}`, encodeURIComponent(String(requestParameters.paymentAccountId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    });
    return new TextApiResponse(response);
  }
  async apiV1PaymentMethodsPlaidBalancePaymentAccountIdGet(requestParameters) {
    const response = await this.apiV1PaymentMethodsPlaidBalancePaymentAccountIdGetRaw(requestParameters);
    return await response.value();
  }
  async apiV1PaymentMethodsPlaidLinkTokenPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/PaymentMethods/Plaid/LinkToken`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.linkTokenRequest
    });
    return new JSONApiResponse(response);
  }
  async apiV1PaymentMethodsPlaidLinkTokenPost(requestParameters) {
    const response = await this.apiV1PaymentMethodsPlaidLinkTokenPostRaw(requestParameters);
    return await response.value();
  }
  async apiV1PaymentMethodsPlaidPublicTokenPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/PaymentMethods/Plaid/PublicToken`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.addPlaidAchAccountRequest
    });
    return new JSONApiResponse(response);
  }
  async apiV1PaymentMethodsPlaidPublicTokenPost(requestParameters) {
    const response = await this.apiV1PaymentMethodsPlaidPublicTokenPostRaw(requestParameters);
    return await response.value();
  }
  async apiV1PaymentMethodsPlaidReauthPaymentAccountIdPostRaw(requestParameters) {
    if (requestParameters.paymentAccountId === null || requestParameters.paymentAccountId === void 0) {
      throw new RequiredError("paymentAccountId", "Required parameter requestParameters.paymentAccountId was null or undefined when calling apiV1PaymentMethodsPlaidReauthPaymentAccountIdPost.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/PaymentMethods/Plaid/Reauth/{paymentAccountId}`.replace(`{${"paymentAccountId"}}`, encodeURIComponent(String(requestParameters.paymentAccountId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    });
    return new VoidApiResponse(response);
  }
  async apiV1PaymentMethodsPlaidReauthPaymentAccountIdPost(requestParameters) {
    await this.apiV1PaymentMethodsPlaidReauthPaymentAccountIdPostRaw(requestParameters);
  }
  async apiV1PaymentMethodsPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/PaymentMethods`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.paymentMethodRequest
    });
    return new JSONApiResponse(response);
  }
  async apiV1PaymentMethodsPost(requestParameters) {
    const response = await this.apiV1PaymentMethodsPostRaw(requestParameters);
    return await response.value();
  }
  async apiV1PaymentMethodsUserOrganizationIdPostRaw(requestParameters) {
    if (requestParameters.organizationId === null || requestParameters.organizationId === void 0) {
      throw new RequiredError("organizationId", "Required parameter requestParameters.organizationId was null or undefined when calling apiV1PaymentMethodsUserOrganizationIdPost.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/PaymentMethods/User/{organizationId}`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.paymentMethodRequest
    });
    return new JSONApiResponse(response);
  }
  async apiV1PaymentMethodsUserOrganizationIdPost(requestParameters) {
    const response = await this.apiV1PaymentMethodsUserOrganizationIdPostRaw(requestParameters);
    return await response.value();
  }
}

class PaymentOptionsApi extends BaseAPI {
  async apiV1AuctionAccessAuctionAccessIdCheckoutPreferencesPaymentOptionsGetRaw(requestParameters) {
    if (requestParameters.auctionAccessId === null || requestParameters.auctionAccessId === void 0) {
      throw new RequiredError("auctionAccessId", "Required parameter requestParameters.auctionAccessId was null or undefined when calling apiV1AuctionAccessAuctionAccessIdCheckoutPreferencesPaymentOptionsGet.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/AuctionAccess/{auctionAccessId}/CheckoutPreferences/PaymentOptions`.replace(`{${"auctionAccessId"}}`, encodeURIComponent(String(requestParameters.auctionAccessId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    });
    return new JSONApiResponse(response);
  }
  async apiV1AuctionAccessAuctionAccessIdCheckoutPreferencesPaymentOptionsGet(requestParameters) {
    const response = await this.apiV1AuctionAccessAuctionAccessIdCheckoutPreferencesPaymentOptionsGetRaw(requestParameters);
    return await response.value();
  }
}

class ProfileApi extends BaseAPI {
  async apiV1ProfileFetchPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Profile/Fetch`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.profileRequest
    });
    return new JSONApiResponse(response);
  }
  async apiV1ProfileFetchPost(requestParameters) {
    const response = await this.apiV1ProfileFetchPostRaw(requestParameters);
    return await response.value();
  }
  async apiV1ProfileGetRaw(requestParameters) {
    const queryParameters = {};
    if (requestParameters.auctionAccessIds) {
      queryParameters["auctionAccessIds"] = requestParameters.auctionAccessIds;
    }
    if (requestParameters.orgIds) {
      queryParameters["orgIds"] = requestParameters.orgIds;
    }
    if (requestParameters.orgs) {
      queryParameters["orgs"] = requestParameters.orgs;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Profile`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    });
    return new JSONApiResponse(response);
  }
  async apiV1ProfileGet(requestParameters) {
    const response = await this.apiV1ProfileGetRaw(requestParameters);
    return await response.value();
  }
}

class RegisterApi extends BaseAPI {
  async apiV1RegisterAccessAddPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Register/Access/Add`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.roleChangeRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1RegisterAccessAddPost(requestParameters) {
    await this.apiV1RegisterAccessAddPostRaw(requestParameters);
  }
  async apiV1RegisterAccessApproveAccessRequestIdGetRaw(requestParameters) {
    if (requestParameters.accessRequestId === null || requestParameters.accessRequestId === void 0) {
      throw new RequiredError("accessRequestId", "Required parameter requestParameters.accessRequestId was null or undefined when calling apiV1RegisterAccessApproveAccessRequestIdGet.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Register/Access/Approve/{accessRequestId}`.replace(`{${"accessRequestId"}}`, encodeURIComponent(String(requestParameters.accessRequestId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    });
    return new VoidApiResponse(response);
  }
  async apiV1RegisterAccessApproveAccessRequestIdGet(requestParameters) {
    await this.apiV1RegisterAccessApproveAccessRequestIdGetRaw(requestParameters);
  }
  async apiV1RegisterAccessDeclineAccessRequestIdGetRaw(requestParameters) {
    if (requestParameters.accessRequestId === null || requestParameters.accessRequestId === void 0) {
      throw new RequiredError("accessRequestId", "Required parameter requestParameters.accessRequestId was null or undefined when calling apiV1RegisterAccessDeclineAccessRequestIdGet.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Register/Access/Decline/{accessRequestId}`.replace(`{${"accessRequestId"}}`, encodeURIComponent(String(requestParameters.accessRequestId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    });
    return new VoidApiResponse(response);
  }
  async apiV1RegisterAccessDeclineAccessRequestIdGet(requestParameters) {
    await this.apiV1RegisterAccessDeclineAccessRequestIdGetRaw(requestParameters);
  }
  async apiV1RegisterAccessRemovePostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Register/Access/Remove`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.roleChangeRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1RegisterAccessRemovePost(requestParameters) {
    await this.apiV1RegisterAccessRemovePostRaw(requestParameters);
  }
  async apiV1RegisterAccessRequestPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Register/Access/Request`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.accessRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1RegisterAccessRequestPost(requestParameters) {
    await this.apiV1RegisterAccessRequestPostRaw(requestParameters);
  }
  async apiV1RegisterAcknowledgeRequestIdPostRaw(requestParameters) {
    if (requestParameters.requestId === null || requestParameters.requestId === void 0) {
      throw new RequiredError("requestId", "Required parameter requestParameters.requestId was null or undefined when calling apiV1RegisterAcknowledgeRequestIdPost.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Register/Acknowledge/{requestId}`.replace(`{${"requestId"}}`, encodeURIComponent(String(requestParameters.requestId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.acknowledgeRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1RegisterAcknowledgeRequestIdPost(requestParameters) {
    await this.apiV1RegisterAcknowledgeRequestIdPostRaw(requestParameters);
  }
  async apiV1RegisterReplayPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Register/Replay`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.registrationReplayRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1RegisterReplayPost(requestParameters) {
    await this.apiV1RegisterReplayPostRaw(requestParameters);
  }
  async apiV1RegisterReplayRequestIdPostRaw(requestParameters) {
    if (requestParameters.requestId === null || requestParameters.requestId === void 0) {
      throw new RequiredError("requestId", "Required parameter requestParameters.requestId was null or undefined when calling apiV1RegisterReplayRequestIdPost.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Register/Replay/{requestId}`.replace(`{${"requestId"}}`, encodeURIComponent(String(requestParameters.requestId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    });
    return new VoidApiResponse(response);
  }
  async apiV1RegisterReplayRequestIdPost(requestParameters) {
    await this.apiV1RegisterReplayRequestIdPostRaw(requestParameters);
  }
}

class RepsApi extends BaseAPI {
  async apiV1RepsMergePostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Reps/Merge`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.repMergeRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1RepsMergePost(requestParameters) {
    await this.apiV1RepsMergePostRaw(requestParameters);
  }
  async apiV1RepsOrgsPostRaw() {
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Reps/Orgs`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    });
    return new JSONApiResponse(response);
  }
  async apiV1RepsOrgsPost() {
    const response = await this.apiV1RepsOrgsPostRaw();
    return await response.value();
  }
  async apiV1RepsPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Reps`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.repCreateRequest
    });
    return new JSONApiResponse(response);
  }
  async apiV1RepsPost(requestParameters) {
    const response = await this.apiV1RepsPostRaw(requestParameters);
    return await response.value();
  }
  async apiV1RepsRepIdGetRaw(requestParameters) {
    if (requestParameters.repId === null || requestParameters.repId === void 0) {
      throw new RequiredError("repId", "Required parameter requestParameters.repId was null or undefined when calling apiV1RepsRepIdGet.");
    }
    const queryParameters = {};
    if (requestParameters.mergeView !== void 0) {
      queryParameters["mergeView"] = requestParameters.mergeView;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Reps/{repId}`.replace(`{${"repId"}}`, encodeURIComponent(String(requestParameters.repId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    });
    return new JSONApiResponse(response);
  }
  async apiV1RepsRepIdGet(requestParameters) {
    const response = await this.apiV1RepsRepIdGetRaw(requestParameters);
    return await response.value();
  }
  async apiV1RepsRepIdPatchRaw(requestParameters) {
    if (requestParameters.repId === null || requestParameters.repId === void 0) {
      throw new RequiredError("repId", "Required parameter requestParameters.repId was null or undefined when calling apiV1RepsRepIdPatch.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Reps/{repId}`.replace(`{${"repId"}}`, encodeURIComponent(String(requestParameters.repId))),
      method: "PATCH",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.repUpdateRequestJsonPatchDocument
    });
    return new JSONApiResponse(response);
  }
  async apiV1RepsRepIdPatch(requestParameters) {
    const response = await this.apiV1RepsRepIdPatchRaw(requestParameters);
    return await response.value();
  }
  async apiV1RepsRepIdPutRaw(requestParameters) {
    if (requestParameters.repId === null || requestParameters.repId === void 0) {
      throw new RequiredError("repId", "Required parameter requestParameters.repId was null or undefined when calling apiV1RepsRepIdPut.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Reps/{repId}`.replace(`{${"repId"}}`, encodeURIComponent(String(requestParameters.repId))),
      method: "PUT",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.repUpdateRequest
    });
    return new JSONApiResponse(response);
  }
  async apiV1RepsRepIdPut(requestParameters) {
    const response = await this.apiV1RepsRepIdPutRaw(requestParameters);
    return await response.value();
  }
  async apiV1RepsUnmergePostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Reps/Unmerge`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.unmergeRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1RepsUnmergePost(requestParameters) {
    await this.apiV1RepsUnmergePostRaw(requestParameters);
  }
  async apiV1RepsVerifyPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Reps/Verify`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.verifyRepRequest
    });
    return new JSONApiResponse(response);
  }
  async apiV1RepsVerifyPost(requestParameters) {
    const response = await this.apiV1RepsVerifyPostRaw(requestParameters);
    return await response.value();
  }
  async apiV1RepsVerifyUserAuctionAccessPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Reps/VerifyUserAuctionAccess`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.verifyRepRequest
    });
    return new JSONApiResponse(response);
  }
  async apiV1RepsVerifyUserAuctionAccessPost(requestParameters) {
    const response = await this.apiV1RepsVerifyUserAuctionAccessPostRaw(requestParameters);
    return await response.value();
  }
}

class TestApi extends BaseAPI {
  async apiV1TestPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Test`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.registrationRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1TestPost(requestParameters) {
    await this.apiV1TestPostRaw(requestParameters);
  }
}

class UploadApi extends BaseAPI {
  async apiV1UploadObjectTypePostRaw(requestParameters) {
    if (requestParameters.objectType === null || requestParameters.objectType === void 0) {
      throw new RequiredError("objectType", "Required parameter requestParameters.objectType was null or undefined when calling apiV1UploadObjectTypePost.");
    }
    const queryParameters = {};
    if (requestParameters.delimiter !== void 0) {
      queryParameters["delimiter"] = requestParameters.delimiter;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const consumes = [{ contentType: "multipart/form-data" }];
    const canConsumeForm$1 = canConsumeForm(consumes);
    let formParams;
    let useForm = false;
    useForm = canConsumeForm$1;
    if (useForm) {
      formParams = new FormData();
    } else {
      formParams = new URLSearchParams();
    }
    if (requestParameters.files) {
      requestParameters.files.forEach((element) => {
        formParams.append("files", element);
      });
    }
    const response = await this.request({
      path: `/api/v1/Upload/{objectType}`.replace(`{${"objectType"}}`, encodeURIComponent(String(requestParameters.objectType))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: formParams
    });
    return new VoidApiResponse(response);
  }
  async apiV1UploadObjectTypePost(requestParameters) {
    await this.apiV1UploadObjectTypePostRaw(requestParameters);
  }
  async apiV1UploadPostRaw(requestParameters) {
    const queryParameters = {};
    if (requestParameters.delimiter !== void 0) {
      queryParameters["delimiter"] = requestParameters.delimiter;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Upload`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    });
    return new VoidApiResponse(response);
  }
  async apiV1UploadPost(requestParameters) {
    await this.apiV1UploadPostRaw(requestParameters);
  }
}

class WebhookApi extends BaseAPI {
  async apiV1WebhookDeleteRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Webhook`,
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.body
    });
    return new VoidApiResponse(response);
  }
  async apiV1WebhookDelete(requestParameters) {
    await this.apiV1WebhookDeleteRaw(requestParameters);
  }
  async apiV1WebhookDeliveriesGetRaw(requestParameters) {
    if (requestParameters.sharedSecret === null || requestParameters.sharedSecret === void 0) {
      throw new RequiredError("sharedSecret", "Required parameter requestParameters.sharedSecret was null or undefined when calling apiV1WebhookDeliveriesGet.");
    }
    if (requestParameters.from === null || requestParameters.from === void 0) {
      throw new RequiredError("from", "Required parameter requestParameters.from was null or undefined when calling apiV1WebhookDeliveriesGet.");
    }
    const queryParameters = {};
    if (requestParameters.sharedSecret !== void 0) {
      queryParameters["SharedSecret"] = requestParameters.sharedSecret;
    }
    if (requestParameters.from !== void 0) {
      queryParameters["From"] = requestParameters.from.toISOString();
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Webhook/Deliveries`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    });
    return new JSONApiResponse(response);
  }
  async apiV1WebhookDeliveriesGet(requestParameters) {
    const response = await this.apiV1WebhookDeliveriesGetRaw(requestParameters);
    return await response.value();
  }
  async apiV1WebhookDocusignPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Webhook/Docusign`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.docuSignWebhookRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1WebhookDocusignPost(requestParameters) {
    await this.apiV1WebhookDocusignPostRaw(requestParameters);
  }
  async apiV1WebhookGetRaw() {
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Webhook`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    });
    return new JSONApiResponse(response);
  }
  async apiV1WebhookGet() {
    const response = await this.apiV1WebhookGetRaw();
    return await response.value();
  }
  async apiV1WebhookPlaidPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Webhook/Plaid`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.plaidWebhookRequest
    });
    return new VoidApiResponse(response);
  }
  async apiV1WebhookPlaidPost(requestParameters) {
    await this.apiV1WebhookPlaidPostRaw(requestParameters);
  }
  async apiV1WebhookPostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Webhook`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.createWebhookSubscriptionRequest
    });
    return new TextApiResponse(response);
  }
  async apiV1WebhookPost(requestParameters) {
    const response = await this.apiV1WebhookPostRaw(requestParameters);
    return await response.value();
  }
  async apiV1WebhookReceivePostRaw(requestParameters) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      if (typeof this.configuration.accessToken === "function") {
        headerParameters["Authorization"] = this.configuration.accessToken("oauth2", []);
      } else {
        headerParameters["Authorization"] = this.configuration.accessToken;
      }
    }
    const response = await this.request({
      path: `/api/v1/Webhook/Receive`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.checkoutPreferenceCreatedPayloadCheckoutPreferenceUpdatedPayloadPaymentMethodUpdatedPayload
    });
    return new VoidApiResponse(response);
  }
  async apiV1WebhookReceivePost(requestParameters) {
    await this.apiV1WebhookReceivePostRaw(requestParameters);
  }
}

var AddressDefaultTypes;
(function(AddressDefaultTypes2) {
  AddressDefaultTypes2["Transportation"] = "Transportation";
  AddressDefaultTypes2["Title"] = "Title";
  AddressDefaultTypes2["Billing"] = "Billing";
  AddressDefaultTypes2["Mailing"] = "Mailing";
})(AddressDefaultTypes || (AddressDefaultTypes = {}));
var AddressType;
(function(AddressType2) {
  AddressType2["Lot"] = "Lot";
  AddressType2["Billing"] = "Billing";
  AddressType2["Mailing"] = "Mailing";
  AddressType2["Payments"] = "Payments";
  AddressType2["Home"] = "Home";
  AddressType2["Physical"] = "Physical";
  AddressType2["BankReferenceLetter"] = "BankReferenceLetter";
  AddressType2["Grounding"] = "Grounding";
  AddressType2["Street"] = "Street";
  AddressType2["Office"] = "Office";
  AddressType2["Corporate"] = "Corporate";
  AddressType2["BankDraft"] = "BankDraft";
  AddressType2["Invoices"] = "Invoices";
  AddressType2["Arbitration"] = "Arbitration";
  AddressType2["Inquires"] = "Inquires";
})(AddressType || (AddressType = {}));
var Agreement;
(function(Agreement2) {
  Agreement2["TermsAndConditions"] = "TermsAndConditions";
  Agreement2["PrivacyPolicy"] = "PrivacyPolicy";
})(Agreement || (Agreement = {}));
var AuctionHubName;
(function(AuctionHubName2) {
  AuctionHubName2["Traderev"] = "TRADEREV";
  AuctionHubName2["Openlane"] = "OPENLANE";
  AuctionHubName2["Physical"] = "PHYSICAL";
  AuctionHubName2["Test"] = "TEST";
  AuctionHubName2["Ereg"] = "EREG";
  AuctionHubName2["Salesforce"] = "SALESFORCE";
  AuctionHubName2["GenericReadonly"] = "GENERIC_READONLY";
  AuctionHubName2["Backlot"] = "BACKLOT";
  AuctionHubName2["Omp"] = "OMP";
})(AuctionHubName || (AuctionHubName = {}));
var AutomaticCheckoutStatus;
(function(AutomaticCheckoutStatus2) {
  AutomaticCheckoutStatus2["Active"] = "Active";
  AutomaticCheckoutStatus2["Inactive"] = "Inactive";
  AutomaticCheckoutStatus2["Deprecated"] = "Deprecated";
  AutomaticCheckoutStatus2["Delinquent"] = "Delinquent";
})(AutomaticCheckoutStatus || (AutomaticCheckoutStatus = {}));
var BusinessType;
(function(BusinessType2) {
  BusinessType2["Independent"] = "Independent";
  BusinessType2["Franchise"] = "Franchise";
})(BusinessType || (BusinessType = {}));
var BusinessUnit;
(function(BusinessUnit2) {
  BusinessUnit2["None"] = "None";
  BusinessUnit2["TradeRev"] = "TradeRev";
  BusinessUnit2["AdesaCa"] = "AdesaCa";
  BusinessUnit2["AdesaCom"] = "AdesaCom";
  BusinessUnit2["Adesa"] = "Adesa";
})(BusinessUnit || (BusinessUnit = {}));
var CsvTypes;
(function(CsvTypes2) {
  CsvTypes2["None"] = "None";
  CsvTypes2["Accountant"] = "Accountant";
  CsvTypes2["BulkAddress"] = "BulkAddress";
  CsvTypes2["OmpOrgs"] = "OmpOrgs";
  CsvTypes2["OmpRoles"] = "OmpRoles";
  CsvTypes2["OrgMerges"] = "OrgMerges";
  CsvTypes2["RepMerges"] = "RepMerges";
})(CsvTypes || (CsvTypes = {}));
var DefaultType;
(function(DefaultType2) {
  DefaultType2["PaymentMethod"] = "PaymentMethod";
  DefaultType2["Transportation"] = "Transportation";
  DefaultType2["Title"] = "Title";
  DefaultType2["Billing"] = "Billing";
  DefaultType2["Mailing"] = "Mailing";
  DefaultType2["NotSet"] = "NotSet";
})(DefaultType || (DefaultType = {}));
var DocuSignWebhookType;
(function(DocuSignWebhookType2) {
  DocuSignWebhookType2["EnvelopeCompleted"] = "EnvelopeCompleted";
})(DocuSignWebhookType || (DocuSignWebhookType = {}));
var DocumentObjectType;
(function(DocumentObjectType2) {
  DocumentObjectType2["Organization"] = "Organization";
  DocumentObjectType2["Rep"] = "Rep";
  DocumentObjectType2["Registration"] = "Registration";
})(DocumentObjectType || (DocumentObjectType = {}));
var DocumentStatus;
(function(DocumentStatus2) {
  DocumentStatus2["Valid"] = "Valid";
  DocumentStatus2["Pending"] = "Pending";
  DocumentStatus2["Rejected"] = "Rejected";
})(DocumentStatus || (DocumentStatus = {}));
var DocumentType;
(function(DocumentType2) {
  DocumentType2["BcPowerOfAttorney"] = "BCPowerOfAttorney";
  DocumentType2["OptionBcTaxExemption2"] = "OptionBCTaxExemption2";
  DocumentType2["BusinessLicense"] = "BusinessLicense";
  DocumentType2["CanadaPowerOfAttorney"] = "CanadaPowerOfAttorney";
  DocumentType2["DealerAgreement"] = "DealerAgreement";
  DocumentType2["DealerLicense"] = "DealerLicense";
  DocumentType2["DriversLicense"] = "DriversLicense";
  DocumentType2["Eft"] = "EFT";
  DocumentType2["FloorplanForm"] = "FloorplanForm";
  DocumentType2["Gst"] = "GST";
  DocumentType2["OlPaymentMethodsForm"] = "OLPaymentMethodsForm";
  DocumentType2["OpenlaneCaApplication"] = "OpenlaneCAApplication";
  DocumentType2["Qst"] = "QST";
  DocumentType2["SalespersonLicense"] = "SalespersonLicense";
  DocumentType2["VoidCheck"] = "VoidCheck";
  DocumentType2["RegistrationPacket"] = "RegistrationPacket";
})(DocumentType || (DocumentType = {}));
var ExportType;
(function(ExportType2) {
  ExportType2["Organization"] = "Organization";
  ExportType2["Rep"] = "Rep";
  ExportType2["Address"] = "Address";
  ExportType2["OrganizationGroup"] = "OrganizationGroup";
})(ExportType || (ExportType = {}));
var Features;
(function(Features2) {
  Features2["Marketguide"] = "Marketguide";
  Features2["MarketplaceCaHard"] = "MarketplaceCaHard";
})(Features || (Features = {}));
var InstitutionalType;
(function(InstitutionalType2) {
  InstitutionalType2["Dealership"] = "Dealership";
  InstitutionalType2["Factory"] = "Factory";
  InstitutionalType2["CaptiveFinance"] = "CaptiveFinance";
  InstitutionalType2["FinancialInstitution"] = "FinancialInstitution";
  InstitutionalType2["FleetLease"] = "FleetLease";
  InstitutionalType2["Government"] = "Government";
  InstitutionalType2["Public"] = "Public";
  InstitutionalType2["Auction"] = "Auction";
})(InstitutionalType || (InstitutionalType = {}));
var Language;
(function(Language2) {
  Language2["En"] = "en";
  Language2["Fr"] = "fr";
})(Language || (Language = {}));
var MergeType;
(function(MergeType2) {
  MergeType2["Rep"] = "Rep";
  MergeType2["Organization"] = "Organization";
})(MergeType || (MergeType = {}));
var ModeType;
(function(ModeType2) {
  ModeType2["None"] = "None";
  ModeType2["Validate"] = "Validate";
  ModeType2["Production"] = "Production";
})(ModeType || (ModeType = {}));
var ObjectType;
(function(ObjectType2) {
  ObjectType2["Address"] = "Address";
  ObjectType2["FinancingAccount"] = "FinancingAccount";
  ObjectType2["Organization"] = "Organization";
  ObjectType2["OrganizationGroup"] = "OrganizationGroup";
  ObjectType2["PaymentMethod"] = "PaymentMethod";
  ObjectType2["Rep"] = "Rep";
  ObjectType2["CreditCard"] = "CreditCard";
  ObjectType2["Registration"] = "Registration";
})(ObjectType || (ObjectType = {}));
var OperationOpEnum;
(function(OperationOpEnum2) {
  OperationOpEnum2["Add"] = "add";
  OperationOpEnum2["Remove"] = "remove";
  OperationOpEnum2["Replace"] = "replace";
  OperationOpEnum2["Move"] = "move";
  OperationOpEnum2["Copy"] = "copy";
  OperationOpEnum2["Test"] = "test";
})(OperationOpEnum || (OperationOpEnum = {}));
var OperationType;
(function(OperationType2) {
  OperationType2["Add"] = "Add";
  OperationType2["Remove"] = "Remove";
  OperationType2["Replace"] = "Replace";
  OperationType2["Move"] = "Move";
  OperationType2["Copy"] = "Copy";
  OperationType2["Test"] = "Test";
  OperationType2["Invalid"] = "Invalid";
})(OperationType || (OperationType = {}));
var PaymentMethodType;
(function(PaymentMethodType2) {
  PaymentMethodType2["EftAch"] = "EftAch";
  PaymentMethodType2["Financing"] = "Financing";
})(PaymentMethodType || (PaymentMethodType = {}));
var ProgramEnrollment;
(function(ProgramEnrollment2) {
  ProgramEnrollment2["Ca"] = "OMP_CA";
  ProgramEnrollment2["Us"] = "OMP_US";
})(ProgramEnrollment || (ProgramEnrollment = {}));
var Region;
(function(Region2) {
  Region2["Gl"] = "GL";
  Region2["Ca"] = "CA";
  Region2["Us"] = "US";
})(Region || (Region = {}));
var RepStatus;
(function(RepStatus2) {
  RepStatus2["Active"] = "Active";
  RepStatus2["Inactive"] = "Inactive";
  RepStatus2["Invited"] = "Invited";
})(RepStatus || (RepStatus = {}));
var RepType;
(function(RepType2) {
  RepType2["Customer"] = "Customer";
  RepType2["Employee"] = "Employee";
})(RepType || (RepType = {}));
var Role;
(function(Role2) {
  Role2["Buyer"] = "Buyer";
  Role2["Seller"] = "Seller";
  Role2["Admin"] = "Admin";
  Role2["Accountant"] = "Accountant";
  Role2["Owner"] = "Owner";
  Role2["Bidder"] = "Bidder";
  Role2["ViewOnly"] = "ViewOnly";
  Role2["InventoryCreator"] = "InventoryCreator";
})(Role || (Role = {}));
var SentDocumentStatus;
(function(SentDocumentStatus2) {
  SentDocumentStatus2["NotSet"] = "NotSet";
  SentDocumentStatus2["InProgress"] = "InProgress";
  SentDocumentStatus2["Completed"] = "Completed";
})(SentDocumentStatus || (SentDocumentStatus = {}));
var SourceSystem;
(function(SourceSystem2) {
  SourceSystem2["Auctionaccess"] = "AUCTIONACCESS";
  SourceSystem2["Plaid"] = "PLAID";
  SourceSystem2["Traderev"] = "TRADEREV";
  SourceSystem2["Openlane"] = "OPENLANE";
  SourceSystem2["Physical"] = "PHYSICAL";
  SourceSystem2["Test"] = "TEST";
  SourceSystem2["Mdm"] = "MDM";
  SourceSystem2["Ec"] = "EC";
  SourceSystem2["EReg"] = "EReg";
  SourceSystem2["Amscustomer"] = "AMSCUSTOMER";
  SourceSystem2["Backlot"] = "BACKLOT";
  SourceSystem2["Afc"] = "AFC";
  SourceSystem2["Salesforce"] = "SALESFORCE";
  SourceSystem2["GenericReadonly"] = "GENERIC_READONLY";
  SourceSystem2["Omp"] = "OMP";
})(SourceSystem || (SourceSystem = {}));
var TaxCode;
(function(TaxCode2) {
  TaxCode2["Hst"] = "HST";
  TaxCode2["Qst"] = "QST";
  TaxCode2["Pst"] = "PST";
  TaxCode2["Rst"] = "RST";
  TaxCode2["Gst"] = "GST";
  TaxCode2["Federal"] = "FEDERAL";
  TaxCode2["State"] = "STATE";
})(TaxCode || (TaxCode = {}));
var UnmergeEntityType;
(function(UnmergeEntityType2) {
  UnmergeEntityType2["Organization"] = "Organization";
  UnmergeEntityType2["Rep"] = "Rep";
})(UnmergeEntityType || (UnmergeEntityType = {}));
var UserType;
(function(UserType2) {
  UserType2["Employee"] = "Employee";
  UserType2["Customer"] = "Customer";
  UserType2["V1"] = "V1";
})(UserType || (UserType = {}));
var VerificationStatus;
(function(VerificationStatus2) {
  VerificationStatus2["Verified"] = "Verified";
  VerificationStatus2["ExistingInOmp"] = "ExistingInOMP";
  VerificationStatus2["NotVerified"] = "NotVerified";
})(VerificationStatus || (VerificationStatus = {}));
var WebhookTopic;
(function(WebhookTopic2) {
  WebhookTopic2["CheckoutPreferenceCreated"] = "CheckoutPreferenceCreated";
  WebhookTopic2["CheckoutPreferenceUpdated"] = "CheckoutPreferenceUpdated";
  WebhookTopic2["PaymentMethodUpdated"] = "PaymentMethodUpdated";
})(WebhookTopic || (WebhookTopic = {}));

export { AccountantsApi, AddressDefaultTypes, AddressType, AddressesApi, Agreement, AuctionHubName, AuthApi, AutomaticCheckoutStatus, BASE_PATH, BaseAPI, BlobApiResponse, BusinessType, BusinessUnit, COLLECTION_FORMATS, CheckoutPreferencesApi, Configuration, CsvTypes, DebugApi, DefaultType, DefaultsApi, DocuSignWebhookType, DocumentObjectType, DocumentStatus, DocumentType, DocumentsApi, ExportType, Features, IdentityApi, InstitutionalType, JSONApiResponse, Language, MergeType, ModeType, ObjectType, OperationOpEnum, OperationType, OrganizationsApi, PaymentMethodType, PaymentMethodsApi, PaymentOptionsApi, ProfileApi, ProgramEnrollment, Region, RegisterApi, RepStatus, RepType, RepsApi, RequiredError, Role, SentDocumentStatus, SourceSystem, TaxCode, TestApi, TextApiResponse, UnmergeEntityType, UploadApi, UserType, VerificationStatus, VoidApiResponse, WebhookApi, WebhookTopic, canConsumeForm, exists, mapValues, querystring };
